import React, { useState } from 'react'
import { map, NoDataDiv, varClass } from '../../shared'
import {
  authorName,
  deleteSuppression,
  formatDate,
  ICommissionSuppression,
  isActiveSuppression,
  suppressionStatus,
} from './api'

// Filters and removes only; the page fetches the list because the Opportunities tab needs it too.
export const SuppressionsView = (props: {
  suppressions: ICommissionSuppression[]
  loaded: boolean
  onChanged: () => void
}) => {
  const { suppressions, loaded, onChanged } = props
  const [filter, setFilter] = useState<'active' | 'all'>('active')
  const [removing, setRemoving] = useState<string | null>(null)

  const rows =
    filter === 'all' ? suppressions : suppressions.filter(isActiveSuppression)

  const remove = async (uuid: string) => {
    setRemoving(uuid)
    await deleteSuppression(uuid)
    setRemoving(null)
    onChanged()
  }

  return (
    <div className="card m-b-20">
      <div className="row row-space-between row-center m-b-15">
        <div className="text-light text-small">
          {rows.length} suppression{rows.length === 1 ? '' : 's'}
        </div>
        <div className="row">
          {(['active', 'all'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={varClass({
                'btn btn-sm m-l-5': true,
                'btn-primary': filter === value,
                'btn-white': filter !== value,
              })}
              onClick={() => setFilter(value)}
            >
              {value === 'active' ? 'Active' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {!loaded ? (
        <div className="loader" />
      ) : rows.length === 0 ? (
        <NoDataDiv
          description={
            filter === 'all'
              ? 'No suppressions in the history.'
              : 'No active suppressions.'
          }
          className="p-y-30"
        />
      ) : (
        <div className="table-container-overflow">
          <table className="table table-bordered table-tight">
            <thead>
              <tr>
                <th>Advertiser</th>
                <th>Status</th>
                <th>Suppressed</th>
                <th>Expires</th>
                <th>By</th>
                <th>Reason</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {map(rows, (row: ICommissionSuppression) => {
                const status = suppressionStatus(row)
                return (
                  <tr key={row.uuid}>
                    <td className="text-dark text-bold">
                      {row.advertiserDomain}
                    </td>
                    <td>
                      <StatusBadge status={status} />
                    </td>
                    <td className="text-nowrap">{formatDate(row.createdAt)}</td>
                    <td className="text-nowrap">{formatDate(row.expiresAt)}</td>
                    <td className="text-nowrap">{authorName(row)}</td>
                    <td className="text-light text-wrap">{row.reason}</td>
                    <td className="text-right">
                      {status === 'active' ? (
                        <button
                          type="button"
                          className="btn btn-white btn-sm"
                          disabled={removing === row.uuid}
                          onClick={() => remove(row.uuid)}
                        >
                          {removing === row.uuid ? 'Removing…' : 'Remove'}
                        </button>
                      ) : (
                        <span className="text-light">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const StatusBadge = (props: { status: 'active' | 'expired' | 'cancelled' }) => {
  if (props.status === 'active') {
    return (
      <span className="value-badge value-badge-green value-badge-sm">
        Active
      </span>
    )
  }
  if (props.status === 'cancelled') {
    return (
      <span className="value-badge value-badge-red value-badge-sm">
        Cancelled
      </span>
    )
  }
  return <span className="badge text-smaller">Expired</span>
}
